import get from 'lodash/get';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import type { IssueListResponse } from '@jira/servicedesk__queues-common/src/IssueListResponse.types.tsx';
import { getUserProperty } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import {
	createResource,
	type CreateResourceArgSync,
	type RouterContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import {
	LOCAL_STORAGE_KEY_CATEGORY,
	LOCAL_STORAGE_KEY_NAV_LOCATION,
	ONE_DAY_IN_MILLIS,
	ONE_HOUR_IN_MILLIS,
	RESOURCE_TYPE_SERVICEDESK_QUEUES_ISSUE_LIST_SSR,
	RESOURCE_TYPE_SERVICEDESK_QUEUES_NAVIGATION_ITEMS,
	RESOURCE_TYPE_SERVICEDESK_QUEUES_PRIORITY_GROUPS,
	SERVICEDESK_QUEUES_NAV_4_SIDEBAR_SELECTION,
	SERVICEDESK_QUEUES_SIDEBAR_QUEUES_CATEGORY,
	SERVICEDESK_QUEUES_SIDEBAR_VIEW_ALL_QUEUES,
} from './resourceTypeServicedeskQueuesNavigationItems.constants.tsx';
import type {
	CategorizedGroupData,
	QueueNavState,
	QueuesCategoryState,
} from './CategorizedQueueData.types.tsx';
import { getPriorityGroupDataByProjectId } from './getPriorityGroupDataByProjectId.tsx';
import {
	getTriageButtonPermission,
	RESOURCE_TYPE_TRIAGE_BUTTON_PERMISSION,
	type TriageButtonPermissionData,
} from './getTriageButtonPermission.tsx';

const getCombinedProjectKeyAndQueueId = ({ match, query }: RouterContext) =>
	`${getProjectKeyId(match, query).projectKey || get(match, 'params.issueKey', '').split('-')[0]}-${get(match, 'params.queueId', '')}`;

const getResourceKeyFromProjectKey = ({ match, query }: Pick<RouterContext, 'match' | 'query'>) =>
	`${getProjectKeyId(match, query).projectKey || get(match, 'params.issueKey', '').split('-')[0]}`;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const queuesNavigationItems: RouteResource<any> = createResource<any>({
	type: RESOURCE_TYPE_SERVICEDESK_QUEUES_NAVIGATION_ITEMS,
	getKey: getResourceKeyFromProjectKey,
	getDataLoader: () =>
		import(
			/* webpackChunkName: "async-resource-servicedesk-queues-nav-items", jiraSpaEntry: "async-resource-servicedesk-queues-nav-items" */ './getData.tsx'
		),
	maxAge: ONE_DAY_IN_MILLIS,
});

export const priorityGroupResource: RouteResource<CategorizedGroupData> =
	createResource<CategorizedGroupData>({
		type: RESOURCE_TYPE_SERVICEDESK_QUEUES_PRIORITY_GROUPS,
		getKey: getResourceKeyFromProjectKey,
		getDataLoader: () =>
			import(
				/* webpackChunkName: "async-resource-servicedesk-queues-priority-group-data-loader", jiraSpaEntry: "async-resource-servicedesk-queues-priority-group-data-loader" */ './getPriorityGroupData.tsx'
			),
		maxAge: ONE_DAY_IN_MILLIS,
		/**
		 * This resource will not load in SSR. Because priority group data resides in the JSM Kukama microservice.
		 * If we wanna fetch this resource during SSR, the request we're making from Tesseract -> Kukama is S2S, where Tesseract needs to know cookies information.
		 * We initially skipped SSR for fetching this resource, because we couldn’t retrieve the request cookie from JFE.
		 * Iiuc, the Jira team doesnt doesnt support this due to security concerns (the info is from JSM Queues team).
		 */
		isBrowserOnly: true,
	});

const priorityGroupByProjectIdResourcePayload: CreateResourceArgSync<CategorizedGroupData> = {
	type: RESOURCE_TYPE_SERVICEDESK_QUEUES_PRIORITY_GROUPS,
	getKey: getResourceKeyFromProjectKey,
	getData: async ({ match, query }) => {
		fireErrorAnalytics({
			meta: {
				id: 'priorityGroupByProjectIdStart',
				packageName: 'jiraRouterResourcesServiceDeskQueues',
				teamName: 'jsm-flash',
			},
			sendToPrivacyUnsafeSplunk: true,
		});

		const { projectKey } = getProjectKeyId(match, query);

		if (!projectKey) {
			fireErrorAnalytics({
				meta: {
					id: 'priorityGroupByProjectIdNoProjectKey',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		return getPriorityGroupDataByProjectId(undefined, projectKey);
	},
	maxAge: ONE_DAY_IN_MILLIS,
};

export const priorityGroupByProjectIdResourceSsr: RouteResource<CategorizedGroupData> =
	createResource<CategorizedGroupData>(priorityGroupByProjectIdResourcePayload);

export const priorityGroupCrossProjectResource: RouteResource<CategorizedGroupData> =
	createResource<CategorizedGroupData>({
		type: RESOURCE_TYPE_SERVICEDESK_QUEUES_PRIORITY_GROUPS,
		getKey: getResourceKeyFromProjectKey,
		getData: async ({ match, query }) => {
			const projectKey = getProjectKeyId(match, query).projectKey;
			try {
				const projectId = match.params.projectId ? +match.params.projectId : undefined;
				return getPriorityGroupDataByProjectId(projectId, projectKey);
			} catch (error) {
				fireErrorAnalytics({
					meta: {
						id: 'priorityGroupCrossProjectNoProjectId',
						packageName: 'jiraRouterResourcesServiceDeskQueues',
						teamName: 'jsm-flash',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
				return getPriorityGroupDataByProjectId(undefined, projectKey);
			}
		},
		maxAge: ONE_DAY_IN_MILLIS,
		isBrowserOnly: true,
	});

export const issueListSsrResource: RouteResource<IssueListResponse | undefined> = createResource<
	IssueListResponse | undefined
>({
	type: RESOURCE_TYPE_SERVICEDESK_QUEUES_ISSUE_LIST_SSR,
	getKey: getCombinedProjectKeyAndQueueId,
	getDataLoader: () =>
		import(
			/* webpackChunkName: "async-resource-servicedesk-queues-issue-list-data-loader", jiraSpaEntry: "async-resource-servicedesk-queues-issue-list-data-loader" */ './fetchIssueListData.tsx'
		),
	// getData below is deliberately set up to load in a sync'd way only in SSR in hope it will execute faster without any delays
	// In CSR we use async data loader above, to not increase jira-spa bundle size for every route
	// oxlint-disable-next-line typescript/no-require-imports, typescript/no-var-requires
	getData: __SERVER__ ? require('./fetchIssueListData.tsx').default : null,
	maxAge: 0,
	// run this resource from server only
	skipBrowser: true,
});

export const queueSidebarViewAllQueuesResource: RouteResource<boolean | undefined> = createResource<
	boolean | undefined
>({
	type: SERVICEDESK_QUEUES_SIDEBAR_VIEW_ALL_QUEUES,
	getKey: getResourceKeyFromProjectKey,
	getData: async ({ match, query }, { tenantContext: { atlassianAccountId } }) => {
		const projectKey = getResourceKeyFromProjectKey({ match, query });

		try {
			if (!atlassianAccountId) {
				throw new Error('accountId is missing.');
			}
			const response = await getUserProperty<boolean>(
				atlassianAccountId,
				`jsm-all-queues-state_is-open-${projectKey}`,
			);
			return response;
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'fetchJsmQueueSidebarViewAllQueuesResourceError',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	},
	maxAge: ONE_DAY_IN_MILLIS,
});

export const queueNav4SidebarSelectionResource: RouteResource<QueueNavState | undefined> =
	createResource<QueueNavState | undefined>({
		type: SERVICEDESK_QUEUES_NAV_4_SIDEBAR_SELECTION,
		getKey: getResourceKeyFromProjectKey,
		getData: async (_, { tenantContext: { atlassianAccountId } }) => {
			try {
				if (!atlassianAccountId) {
					throw new Error('accountId is missing.');
				}
				const response = await getUserProperty<QueueNavState>(
					atlassianAccountId,
					LOCAL_STORAGE_KEY_NAV_LOCATION,
				);
				return response;
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'fetchQueueNav4SidebarSelectionResourceError',
						packageName: 'jiraRouterResourcesServiceDeskQueues',
						teamName: 'jsm-flash',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		maxAge: ONE_DAY_IN_MILLIS,
	});

export const queueSidebarQueuesCategoryStateResource: RouteResource<
	QueuesCategoryState | undefined
> = createResource<QueuesCategoryState | undefined>({
	type: SERVICEDESK_QUEUES_SIDEBAR_QUEUES_CATEGORY,
	getKey: getResourceKeyFromProjectKey,
	getData: async (_, { tenantContext: { atlassianAccountId } }) => {
		try {
			if (!atlassianAccountId) {
				throw new Error('accountId is missing.');
			}
			const response = await getUserProperty<QueuesCategoryState>(
				atlassianAccountId,
				LOCAL_STORAGE_KEY_CATEGORY,
			);
			return response;
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'fetchJsmQueueSidebarQueuesCategoryStateResourceError',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
					teamName: 'jsm-flash',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	},
	maxAge: ONE_DAY_IN_MILLIS,
});

export const triageButtonPermissionResource: RouteResource<TriageButtonPermissionData> =
	createResource<TriageButtonPermissionData>({
		type: RESOURCE_TYPE_TRIAGE_BUTTON_PERMISSION,
		getKey: () => RESOURCE_TYPE_TRIAGE_BUTTON_PERMISSION,
		getData: async () => {
			return getTriageButtonPermission();
		},
		maxAge: ONE_HOUR_IN_MILLIS,
	});
